import { api } from '../../../servicesGlobais/api'
import { Pessoa, PessoaFormData, PessoaFilters, CargoDisponivel, PessoaParametrizacao, CriarParametrizacaoPayload, TipoContratacao, TipoPremiacao, TipoParametroPessoa } from '../tipos/pessoa'

function normalizePessoa(backendPessoa: any): Pessoa {
  if (!backendPessoa) {
    return {
      nome: '',
      email: '',
      ativo: true,
    }
  }

  const timeId = backendPessoa.timeId ?? backendPessoa.TimeId ?? undefined
  return {
    idPessoa: backendPessoa.idPessoa ?? backendPessoa.IdPessoa ?? undefined,
    nome: backendPessoa.nome ?? backendPessoa.Nome ?? '',
    email: backendPessoa.email ?? backendPessoa.Email ?? '',
    telefone: backendPessoa.telefone ?? backendPessoa.Telefone ?? undefined,
    observacao: backendPessoa.observacao ?? backendPessoa.Observacao ?? undefined,
    cargoCodigo: backendPessoa.cargoCodigo ?? backendPessoa.CargoCodigo ?? undefined,
    cargoNome: backendPessoa.cargoNome ?? backendPessoa.CargoNome ?? undefined,
    ativo: backendPessoa.ativo ?? backendPessoa.Ativo ?? true,
    dataCadastro: backendPessoa.dataCadastro ?? backendPessoa.DataCadastro ?? undefined,
    dataUltimaAtualizacao: backendPessoa.dataUltimaAtualizacao ?? backendPessoa.DataUltimaAtualizacao ?? undefined,
    timeId,
    tipoContratacaoDescricao: backendPessoa.tipoContratacaoDescricao ?? backendPessoa.TipoContratacaoDescricao ?? undefined,
    tipoPremiacaoDescricao: backendPessoa.tipoPremiacaoDescricao ?? backendPessoa.TipoPremiacaoDescricao ?? undefined,
    valorHoraAtual: backendPessoa.valorHoraAtual ?? backendPessoa.ValorHoraAtual ?? undefined,
  }
}

function convertToFormData(backendPessoa: any): PessoaFormData {
  const normalized = normalizePessoa(backendPessoa)

  return {
    idPessoa: normalized.idPessoa,
    nome: normalized.nome,
    email: normalized.email,
    telefone: normalized.telefone,
    observacao: normalized.observacao,
    cargoCodigo: normalized.cargoCodigo ? String(normalized.cargoCodigo) : '',
    ativo: normalized.ativo,
    timeId: normalized.timeId != null ? String(normalized.timeId) : '',
  }
}

function convertToBackend(pessoa: PessoaFormData) {
  return {
    idPessoa: pessoa.idPessoa ?? 0,
    nome: pessoa.nome,
    email: pessoa.email,
    telefone: pessoa.telefone || undefined,
    observacao: pessoa.observacao || undefined,
    cargoCodigo: pessoa.cargoCodigo && pessoa.cargoCodigo !== '' ? Number(pessoa.cargoCodigo) : undefined,
    ativo: pessoa.ativo,
    timeId: pessoa.timeId && pessoa.timeId !== '' ? Number(pessoa.timeId) : undefined,
  }
}

function filtrarPessoasLocalmente(pessoas: Pessoa[], filters?: PessoaFilters): Pessoa[] {
  let resultado = [...pessoas]

  if (filters?.search) {
    const searchTerm = filters.search.toLowerCase()
    resultado = resultado.filter(
      (p) =>
        p.nome.toLowerCase().includes(searchTerm) ||
        p.email.toLowerCase().includes(searchTerm) ||
        p.telefone?.toLowerCase().includes(searchTerm) ||
        p.cargoNome?.toLowerCase().includes(searchTerm)
    )
  }

  if (filters?.ativo && filters.ativo !== 'todos') {
    const ativo = filters.ativo === 'sim'
    resultado = resultado.filter((p) => p.ativo === ativo)
  }

  if (filters?.cargoCodigo && filters.cargoCodigo !== 'todos') {
    const cargoCodigo = Number(filters.cargoCodigo)
    resultado = resultado.filter((p) => p.cargoCodigo === cargoCodigo)
  }

  if (filters?.timeId && filters.timeId !== '' && filters.timeId !== 'todos') {
    const timeId = Number(filters.timeId)
    resultado = resultado.filter((p) => p.timeId === timeId)
  }

  return resultado
}

export const pessoaService = {
  async listarPessoas(filters?: PessoaFilters, incluirExternos = false): Promise<Pessoa[]> {
    const params = new URLSearchParams()
    if (filters?.ativo === 'sim') params.append('apenasAtivos', 'true')
    if (filters?.cargoCodigo && filters.cargoCodigo !== 'todos') {
      params.append('cargoCodigo', filters.cargoCodigo)
    }
    if (filters?.timeId && filters.timeId !== '') {
      params.append('timeId', filters.timeId)
    }
    // Rotinas de manutenção (Pessoas/Times) exibem também pessoas de times externos.
    if (incluirExternos) params.append('incluirExternos', 'true')

    const pessoas = await api.get<any[]>(`/Pessoa${params.toString() ? `?${params.toString()}` : ''}`)
    const normalizados = Array.isArray(pessoas) ? pessoas.map(normalizePessoa) : []
    return filtrarPessoasLocalmente(normalizados, filters)
  },

  async getPessoaById(id: number): Promise<PessoaFormData> {
    const pessoa = await api.get<any>(`/Pessoa/${id}`)
    return convertToFormData(pessoa)
  },

  async criarPessoa(data: PessoaFormData): Promise<Pessoa> {
    const backendData = convertToBackend(data)
    const created = await api.post<any>('/Pessoa', backendData)
    return normalizePessoa(created)
  },

  async atualizarPessoa(id: number, data: PessoaFormData): Promise<Pessoa> {
    const backendData = convertToBackend({ ...data, idPessoa: id })
    const updated = await api.put<any>(`/Pessoa/${id}`, backendData)
    return normalizePessoa(updated)
  },

  async excluirPessoa(id: number): Promise<void> {
    await api.delete(`/Pessoa/${id}`)
  },

  async listarCargos(): Promise<CargoDisponivel[]> {
    const cargos = await api.get<any[]>('/Pessoa/cargos')
    if (!Array.isArray(cargos)) return []

    return cargos.map((cargo: any) => ({
      codigo: Number(cargo.codigo ?? cargo.Codigo ?? 0),
      nome: cargo.nome ?? cargo.Nome ?? '',
      descricao: cargo.descricao ?? cargo.Descricao ?? undefined,
    }))
  },

  async listarTiposContratacao(): Promise<TipoContratacao[]> {
    const tipos = await api.get<any[]>('/Pessoa/tipos-contratacao')
    if (!Array.isArray(tipos)) return []
    return tipos.map((t: any) => ({
      codigo: Number(t.codigo ?? t.Codigo ?? 0),
      descricao: t.descricao ?? t.Descricao ?? '',
    }))
  },

  async listarTiposPremiacao(): Promise<TipoPremiacao[]> {
    const tipos = await api.get<any[]>('/Pessoa/tipos-premiacao')
    if (!Array.isArray(tipos)) return []
    return tipos.map((t: any) => ({
      codigo: Number(t.codigo ?? t.Codigo ?? 0),
      descricao: t.descricao ?? t.Descricao ?? '',
    }))
  },

  async listarParametrizacoes(pessoaId: number, tipoParametro?: TipoParametroPessoa): Promise<PessoaParametrizacao[]> {
    const params = tipoParametro != null ? `?tipoParametro=${tipoParametro}` : ''
    const data = await api.get<any[]>(`/Pessoa/${pessoaId}/parametrizacoes${params}`)
    if (!Array.isArray(data)) return []
    return data.map((p: any) => ({
      id: p.id ?? p.Id ?? 0,
      pessoaId: p.pessoaId ?? p.PessoaId ?? 0,
      tipoParametro: p.tipoParametro ?? p.TipoParametro ?? 1,
      tipoContratacaoCodigo: p.tipoContratacaoCodigo ?? p.TipoContratacaoCodigo ?? undefined,
      tipoContratacaoDescricao: p.tipoContratacaoDescricao ?? p.TipoContratacaoDescricao ?? undefined,
      tipoPremiacaoCodigo: p.tipoPremiacaoCodigo ?? p.TipoPremiacaoCodigo ?? undefined,
      tipoPremiacaoDescricao: p.tipoPremiacaoDescricao ?? p.TipoPremiacaoDescricao ?? undefined,
      valorHora: p.valorHora ?? p.ValorHora ?? undefined,
      dataInicioVigencia: p.dataInicioVigencia ?? p.DataInicioVigencia ?? '',
      dataFimVigencia: p.dataFimVigencia ?? p.DataFimVigencia ?? undefined,
    }))
  },

  async criarParametrizacao(pessoaId: number, data: CriarParametrizacaoPayload): Promise<PessoaParametrizacao> {
    const payload = {
      pessoaId,
      tipoParametro: data.tipoParametro,
      tipoContratacaoCodigo: data.tipoContratacaoCodigo,
      tipoPremiacaoCodigo: data.tipoPremiacaoCodigo,
      valorHora: data.valorHora,
      dataInicioVigencia: data.dataInicioVigencia,
    }
    return await api.post<PessoaParametrizacao>(`/Pessoa/${pessoaId}/parametrizacao`, payload)
  },
}
